// External libraries
import Grid from '@mui/material/Grid2';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// API
import { useCreateQuantityUnitMutation, useUpdateQuantityUnitMutation } from '@api/quantity-units/quantity-units-api';

// Models
import { QuantityUnitApiModel } from '@models/ApiModels/quantity-unit-api-model';
import { QuantityUnit } from '@models/DomainModels/Master/quantity-unit';

// Common components
import { CdfFormField } from '@commonComponents/forms/fields/CdfFormField';
import { EntityDetailDialog, EntityDetailDialogWrapper } from '@commonComponents/dialogs/EntityDetailDialog';

// State management
import { DynamicStore } from '@state/interfaces/idynamic-store';

// Form Validation
import { FormFactory } from '@services/validation/form-factory';
import { useDecoratorForm } from '@hooks/useDecoratorForm';
import { useEntityDetail, EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

// Permissions
import { useFetchToken } from '@hooks/useFetchToken';

// Props interface for the QuantityUnitDetailDialog component
type QuantityUnitDetailDialogProps = {
  quantityUnitData?: QuantityUnit;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<QuantityUnit>) => void;
  store: DynamicStore | undefined;
  token: string;
  readPermission?: boolean;
  writePermission?: boolean;
};

function QuantityUnitDetailDialog({
  quantityUnitData,
  organizationId,
  open,
  onClose,
  token,
  readPermission = false,
  writePermission = false
}: Readonly<QuantityUnitDetailDialogProps>) {
  const { t } = useTranslation(['quantityUnits', 'common']);

  // RTK Query hooks
  const [createQuantityUnit] = useCreateQuantityUnitMutation();
  const [updateQuantityUnit] = useUpdateQuantityUnitMutation();

  // Initialize form using FormFactory
  const formConfig = FormFactory.getFormConfig(
    QuantityUnitApiModel,
    quantityUnitData ?? {
      number: 0,
      name: '',
      abbreviation: ''
    },
    { showSnackbarOnError: true }
  );

  const {
    data: quantityUnit,
    errors: validationErrors,
    isValid: formIsValid,
    updateField,
    validateField,
    validateForm,
    resetForm,
    setData,
    setFormChanged,
    isDirty
  } = useDecoratorForm(formConfig.modelClass, formConfig.initialData, formConfig.options);

  // Memoize the isDirty value to prevent excessive re-renders
  const isDirtyValue = useMemo(() => isDirty(), [isDirty]);

  // Check for required fields having values
  // This ensures the save button is disabled until all required fields have values
  const hasRequiredFieldValues = useMemo(() => {
    return (
      // Check for number - must either be a non-zero number or a valid number string
      ((typeof quantityUnit.number === 'number' && quantityUnit.number !== 0) ||
        (typeof quantityUnit.number === 'string' && quantityUnit.number !== '' && !Number.isNaN(Number(quantityUnit.number)))) &&
      // Check for name string
      !!quantityUnit.name?.trim() &&
      // Check for abbreviation string
      !!quantityUnit.abbreviation?.trim()
    );
  }, [quantityUnit.number, quantityUnit.name, quantityUnit.abbreviation]);

  // Combine validations - the form is valid only when all required fields are filled AND validation passes
  const isValid = useMemo(() => {
    return formIsValid && hasRequiredFieldValues;
  }, [formIsValid, hasRequiredFieldValues]);

  // Create an enhanced wrapper for validateForm with better logging and error handling
  const validateFormWrapper = useCallback(async (): Promise<boolean> => {
    // Run the decorator-based validation
    return (await validateForm()).isValid;
  }, [validateForm]);

  const createEntity = useCallback(async (): Promise<QuantityUnit> => {
    const createdId = await createQuantityUnit({
      organizationId,
      quantityUnitData: {
        number: quantityUnit.number ?? 0,
        name: quantityUnit.name ?? '',
        abbreviation: quantityUnit.abbreviation ?? ''
      },
      token
    }).unwrap();

    return {
      id: createdId,
      organizationId,
      number: quantityUnit.number ?? 0,
      name: quantityUnit.name ?? '',
      abbreviation: quantityUnit.abbreviation ?? ''
    } as QuantityUnit;
  }, [createQuantityUnit, organizationId, token, quantityUnit]);

  // Update entity function
  const updateEntity = useCallback(async (): Promise<QuantityUnit> => {
    await updateQuantityUnit({
      organizationId,
      quantityUnitId: quantityUnitData?.id ?? '',
      quantityUnitData: {
        number: quantityUnit.number ?? 0,
        name: quantityUnit.name ?? '',
        abbreviation: quantityUnit.abbreviation ?? ''
      },
      token
    }).unwrap();

    // Return the updated entity
    return {
      ...quantityUnit,
      id: quantityUnitData?.id ?? '',
      organizationId
    } as QuantityUnit;
  }, [updateQuantityUnit, organizationId, quantityUnitData?.id, token, quantityUnit]);

  // Create a wrapped onClose function that ensures the form is reset
  const handleDialogClose = useCallback(
    (result: EntityDialogCloseResult<QuantityUnit>) => {
      resetForm();
      setFormChanged(false);
      onClose(result);
    },
    [onClose, resetForm, setFormChanged]
  );

  // Use the entity detail hook
  const { handleSave, loadingState } = useEntityDetail<QuantityUnit>({
    entityName: t('entityName'),
    initialData: (quantityUnitData ?? { name: '', abbreviation: '' }) as QuantityUnit,
    open,
    onClose: handleDialogClose,
    createEntity,
    updateEntity,
    validateForm: validateFormWrapper,
    setData: data => setData(data as Partial<QuantityUnitApiModel>, true),
    resetForm,
    loggerContext: 'QuantityUnitDetailDialog',
    readPermission,
    writePermission,
    setFormChanged
  });

  const handleFieldChange = useCallback(
    (field: string, value: string | number) => {
      const processedValue = field === 'number' ? Number(value) || 0 : value;
      updateField(field, processedValue);
      setTimeout(() => validateField(field, processedValue), 0);
    },
    [updateField, validateField]
  );

  // Handle field blur (validation)
  const handleFieldBlur = useCallback(
    (field: string) => {
      validateField(field, quantityUnit[field as keyof QuantityUnitApiModel]);
    },
    [validateField, quantityUnit]
  );

  // Create form content
  const formContent = (
    <Grid container size={12}>
      <Grid size={12}>
        <CdfFormField
          name="number"
          label={t('detailDialog.number')}
          value={quantityUnit.number}
          required
          type="number"
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="number-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.number'),
              readOnly: !writePermission
            }
          }}
        />
      </Grid>

      {/* Name Field */}
      <Grid size={12}>
        <CdfFormField
          name="name"
          label={t('detailDialog.name')}
          value={quantityUnit.name}
          required
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="name-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.name'),
              readOnly: !writePermission
            }
          }}
        />
      </Grid>

      {/* Abbreviation Field */}
      <Grid size={12}>
        <CdfFormField
          name="abbreviation"
          label={t('detailDialog.abbreviation')}
          value={quantityUnit.abbreviation}
          required
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="abbreviation-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.abbreviation'),
              readOnly: !writePermission
            }
          }}
        />
      </Grid>
    </Grid>
  );

  // Get save button text based on edit vs. create mode
  const saveButtonText = quantityUnitData?.id ? t('saveChanges') : t('addButtonText');

  // Get cancel button text based on write permission
  const cancelButtonText = writePermission ? t('common:cancel') : t('common:close');

  // Get edit mode display title based on permissions
  const editModeTitle = writePermission ? t('edit') : t('view');

  // Get title text based on whether we're editing or creating
  const titleText = quantityUnitData?.id ? editModeTitle : t('add');

  return (
    <EntityDetailDialog
      open={open}
      onClose={() => handleDialogClose({ action: 'cancelled' })}
      title={titleText}
      formContent={formContent}
      saveButtonText={saveButtonText}
      cancelButtonText={cancelButtonText}
      readPermission={readPermission}
      writePermission={writePermission}
      isLoading={false}
      loadingState={loadingState}
      loadingMessage={t('detailDialog.loadingMessage')}
      savingMessage={t('detailDialog.savingMessage')}
      isValid={isValid}
      isDirty={isDirtyValue}
      onSave={handleSave}
      dataTestId="quantity-unit-detail-dialog"
      maxWidth="xs"
    />
  );
}

// Props interface for the QuantityUnitDetailDialogWrapper component
type QuantityUnitDetailDialogWrapperProps = {
  quantityUnitData?: QuantityUnit;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<QuantityUnit>) => void;
  store: DynamicStore | undefined;
};

export function QuantityUnitDetailDialogWrapper({
  quantityUnitData,
  open,
  onClose,
  organizationId,
  store
}: Readonly<QuantityUnitDetailDialogWrapperProps>) {
  const token = useFetchToken();

  return (
    <EntityDetailDialogWrapper
      organizationId={organizationId}
      store={store}
      loggerContext="QuantityUnitDetailDialog"
      dataTestId="quantity-unit-detail-error-boundary-wrapper"
      entityType="QuantityUnits"
      renderContent={permissions => (
        <QuantityUnitDetailDialog
          quantityUnitData={quantityUnitData}
          open={open}
          onClose={onClose}
          organizationId={organizationId}
          store={store}
          token={token}
          readPermission={permissions.readPermission}
          writePermission={permissions.writePermission}
        />
      )}
    />
  );
}
